import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, BarChart3, Calendar, CheckCircle2, Clock, Download, FileBadge, FileText, Layers, Loader2, MapPin, Music, Plus, RefreshCw, Send, Sparkles, Trash2, Trophy, Users, X } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import Layout from "../../components/Layout";
import api, { downloadFile, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../../components/ui/alert-dialog";

const LATIN_DANCE_OPTIONS = [
  { code: "samba", label: "Samba" },
  { code: "rumba", label: "Rumba" },
  { code: "cha_cha_cha", label: "Cha Cha Cha" },
  { code: "jive", label: "Jive" },
  { code: "paso_doble", label: "Paso Doble" },
];
const LATIN_CLASSI = ["AS", "A", "B", "C", "D", "E"];
const LATIN_FASCE = ["8/9", "10/11", "12/13", "14/15", "16/18", "19/34", "22/30", "Under 21"];
const SPLIT_MODES = [
  { v: "random", label: "Random" },
  { v: "classe", label: "per Classe" },
  { v: "eta", label: "per Età" },
  { v: "classe_eta", label: "per Classe ed Età" },
];

export default function CompetizioneDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [comp, setComp] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Judge rows: array of { key, judgeId | null }. judgeId=null means empty row awaiting selection.
  const [judgeRows, setJudgeRows] = useState([]);
  const [savingJudges, setSavingJudges] = useState(false);

  const [athleteForm, setAthleteForm] = useState({ number: "", name: "", type: "Singolo", classe: "", fascia_eta: "" });
  const [adding, setAdding] = useState(false);
  const [sending, setSending] = useState(false);
  const [savingLatinConfig, setSavingLatinConfig] = useState(false);
  const [generatingBatches, setGeneratingBatches] = useState(false);
  const [downloadingBatchesPdf, setDownloadingBatchesPdf] = useState(false);

  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [downloadingZip, setDownloadingZip] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  const handleDownloadAthlete = async (athleteId, athleteName) => {
    setDownloadingId(athleteId);
    try {
      await downloadFile(`/competitions/${id}/certificates/${athleteId}`, `attestato_${athleteName}.pdf`);
      toast.success("Attestato scaricato");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDownloadAll = async () => {
    setDownloadingZip(true);
    try {
      await downloadFile(`/competitions/${id}/certificates.zip`, "attestati.zip");
      toast.success("Download avviato");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDownloadingZip(false);
    }
  };

  const isSent = comp?.sent === true;
  const isLatin = comp?.type === "latin";
  const compDances = useMemo(() => comp?.dances || [], [comp]);
  const compBatches = useMemo(() => comp?.batches || [], [comp]);

  const toggleDance = async (code) => {
    if (isSent) return;
    const current = compDances;
    const next = current.includes(code) ? current.filter((d) => d !== code) : [...current, code];
    setSavingLatinConfig(true);
    try {
      const { data } = await api.put(`/competitions/${id}/latin-config`, {
        dances: next,
        batches_mode: comp.batches_mode || "single",
        batches_count: comp.batches_count || 1,
        batches_split_by: comp.batches_split_by || "random",
      });
      setComp(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingLatinConfig(false);
    }
  };

  const updateBatchesConfig = async (patch) => {
    setSavingLatinConfig(true);
    try {
      const { data } = await api.put(`/competitions/${id}/latin-config`, {
        dances: compDances,
        batches_mode: patch.batches_mode ?? comp.batches_mode ?? "single",
        batches_count: patch.batches_count ?? comp.batches_count ?? 1,
        batches_split_by: patch.batches_split_by ?? comp.batches_split_by ?? "random",
      });
      setComp(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingLatinConfig(false);
    }
  };

  const onGenerateBatches = async () => {
    setGeneratingBatches(true);
    try {
      await api.post(`/competitions/${id}/batches/generate`);
      toast.success("Batterie generate");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setGeneratingBatches(false);
    }
  };

  const onMoveAthlete = async (athleteId, targetBatchId) => {
    try {
      const { data } = await api.put(`/competitions/${id}/batches/move`, {
        athlete_id: athleteId,
        target_batch_id: targetBatchId,
      });
      setComp((c) => ({ ...c, batches: data.batches, batches_count: data.batches.length }));
      if (targetBatchId === "new") {
        toast.success("Nuova batteria creata");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const onDownloadBatchesPdf = async () => {
    setDownloadingBatchesPdf(true);
    try {
      await downloadFile(`/competitions/${id}/batches.pdf`, `batterie_${comp.name}.pdf`);
      toast.success("Download avviato");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDownloadingBatchesPdf(false);
    }
  };

  const loadSummary = async () => {
    setSummaryLoading(true);
    try {
      const { data } = await api.get(`/competitions/${id}/judging-summary`);
      setSummary(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSummaryLoading(false);
    }
  };

  const load = async () => {
    try {
      const [c, u] = await Promise.all([api.get(`/competitions/${id}`), api.get("/users")]);
      setComp(c.data);
      setAllUsers(u.data);
      const judges = c.data.judges || [];
      setJudgeRows(judges.length ? judges.map((jid, i) => ({ key: `j-${i}-${jid}`, judgeId: jid }))
                                  : [{ key: `j-empty-${Date.now()}`, judgeId: null }]);
    } catch (err) {
      toast.error(formatApiError(err));
      navigate("/responsabile/competizioni", { replace: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  useEffect(() => {
    if (isSent) loadSummary();
    /* eslint-disable-next-line */
  }, [isSent]);

  const sortedAthletes = useMemo(() => {
    return [...(comp?.athletes || [])].sort((a, b) => a.number.localeCompare(b.number));
  }, [comp]);

  const selectedJudgeIds = useMemo(
    () => judgeRows.map((r) => r.judgeId).filter(Boolean),
    [judgeRows]
  );

  const persistJudges = async (ids) => {
    setSavingJudges(true);
    try {
      const { data } = await api.put(`/competitions/${id}/judges`, { judge_ids: ids });
      setComp(data);
      return true;
    } catch (err) {
      toast.error(formatApiError(err));
      return false;
    } finally {
      setSavingJudges(false);
    }
  };

  const onSelectJudge = async (rowKey, newJudgeId) => {
    if (selectedJudgeIds.includes(newJudgeId)) {
      toast.error("Giudice già selezionato");
      return;
    }
    const updated = judgeRows.map((r) => r.key === rowKey ? { ...r, judgeId: newJudgeId } : r);
    setJudgeRows(updated);
    const ids = updated.map((r) => r.judgeId).filter(Boolean);
    const ok = await persistJudges(ids);
    if (ok) toast.success("Giudice aggiunto");
  };

  const onAddRow = () => {
    // Don't allow more than one empty row
    if (judgeRows.some((r) => !r.judgeId)) {
      toast.info("Compila prima la riga vuota");
      return;
    }
    if (selectedJudgeIds.length >= allUsers.length) {
      toast.info("Tutti gli utenti disponibili sono già stati selezionati");
      return;
    }
    setJudgeRows([...judgeRows, { key: `j-new-${Date.now()}`, judgeId: null }]);
  };

  const onRemoveRow = async (rowKey) => {
    const target = judgeRows.find((r) => r.key === rowKey);
    const updated = judgeRows.filter((r) => r.key !== rowKey);
    // Always keep at least one row visible (empty)
    const final = updated.length === 0 ? [{ key: `j-empty-${Date.now()}`, judgeId: null }] : updated;
    setJudgeRows(final);
    if (target?.judgeId) {
      const ids = final.map((r) => r.judgeId).filter(Boolean);
      const ok = await persistJudges(ids);
      if (ok) toast.success("Giudice rimosso");
    }
  };

  const onAddAthlete = async (e) => {
    e.preventDefault();
    const num = parseInt(athleteForm.number, 10);
    if (!num || num < 1 || num > 600) {
      toast.error("Numero di gara: tra 1 e 600");
      return;
    }
    if (!athleteForm.name.trim()) {
      toast.error("Inserire Nome e Cognome");
      return;
    }
    if (isLatin) {
      if (!athleteForm.classe) {
        toast.error("Seleziona la Classe");
        return;
      }
      if (!athleteForm.fascia_eta) {
        toast.error("Seleziona la Fascia di età");
        return;
      }
    }
    setAdding(true);
    try {
      const payload = {
        number: num,
        name: athleteForm.name,
        type: athleteForm.type,
      };
      if (isLatin) {
        payload.classe = athleteForm.classe;
        payload.fascia_eta = athleteForm.fascia_eta;
      }
      await api.post(`/competitions/${id}/athletes`, payload);
      setAthleteForm({ number: "", name: "", type: isLatin ? "Singolo" : "Singolo", classe: "", fascia_eta: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAdding(false);
    }
  };

  const onDeleteAthlete = async (athId) => {
    try {
      await api.delete(`/competitions/${id}/athletes/${athId}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const onSend = async () => {
    setSending(true);
    try {
      await api.post(`/competitions/${id}/send`);
      toast.success("Competizione inviata ai giudici");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  if (loading || !comp) {
    return (
      <Layout>
        <div className="p-10 text-center text-slate-500 text-sm">Caricamento…</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Link to="/responsabile/competizioni" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-6" data-testid="back-to-comps">
        <ArrowLeft className="h-4 w-4" /> Le mie competizioni
      </Link>

      <div className="bg-white rounded-2xl ring-1 ring-slate-200 p-6 sm:p-8 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-xl bg-amber-50 text-amber-700 flex items-center justify-center">
              <Trophy className="h-6 w-6" />
            </div>
            <div>
              <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">{comp.name}</h1>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-slate-600 mt-2">
                <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{comp.location}</span>
                <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" />{new Date(comp.date).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" })}</span>
              </div>
            </div>
          </div>
          {isSent ? (
            <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 self-start">Competizione inviata</Badge>
          ) : (
            <Badge variant="outline" className="border-slate-300 text-slate-600 self-start">Bozza</Badge>
          )}
        </div>
        {isSent && (
          <p className="text-xs text-slate-500 mt-4 italic">
            Modifiche a giudici e atleti saranno aggiornate in tempo reale agli utenti destinatari.
          </p>
        )}
      </div>

      {/* Step 1 - Judges */}
      <section className="bg-white rounded-2xl ring-1 ring-slate-200 p-6 sm:p-8 mb-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center font-display font-bold">1</div>
            <div>
              <h2 className="font-display text-xl font-semibold text-slate-900">Pannello giudicante</h2>
              <p className="text-sm text-slate-500">Seleziona i giudici dalle utenze. Aggiungi righe con il pulsante +.</p>
            </div>
          </div>
          <div className="flex items-center gap-3 text-xs text-slate-500">
            <Users className="h-3.5 w-3.5" />
            <span data-testid="judges-count">{selectedJudgeIds.length} giudic{selectedJudgeIds.length === 1 ? "e" : "i"} selezionat{selectedJudgeIds.length === 1 ? "o" : "i"}</span>
            {savingJudges && <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-600" />}
          </div>
        </div>

        <div className="mt-5 space-y-2.5">
          {judgeRows.map((row, idx) => {
            const otherSelected = selectedJudgeIds.filter((jid) => jid !== row.judgeId);
            const availableUsers = allUsers.filter((u) => !otherSelected.includes(u.id));
            return (
              <div key={row.key} className="flex items-center gap-2" data-testid={`judge-row-${idx}`}>
                <div className="flex-shrink-0 h-10 w-10 rounded-lg bg-slate-50 text-slate-500 flex items-center justify-center font-mono text-sm font-semibold">
                  {idx + 1}
                </div>
                <Select
                  value={row.judgeId || ""}
                  onValueChange={(v) => onSelectJudge(row.key, v)}
                  disabled={savingJudges}
                >
                  <SelectTrigger className="h-11 flex-1 bg-white" data-testid={`judge-select-${idx}`}>
                    <SelectValue placeholder="Seleziona giudice…" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {availableUsers.map((u) => (
                      <SelectItem key={u.id} value={u.id} data-testid={`judge-option-${u.id}`}>
                        {u.name} <span className="text-slate-400">— {u.email}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {row.judgeId ? (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="text-rose-600 hover:bg-rose-50 hover:text-rose-700 flex-shrink-0"
                        data-testid={`remove-judge-${idx}`}
                        aria-label="Rimuovi giudice"
                      >
                        <X className="h-5 w-5" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Rimuovere il giudice?</AlertDialogTitle>
                        <AlertDialogDescription>
                          {allUsers.find((u) => u.id === row.judgeId)?.name} verrà rimosso dal pannello giudicante.
                          {isSent && " La competizione è già stata inviata: il giudice non la vedrà più nella sua area Dance Analysis."}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Annulla</AlertDialogCancel>
                        <AlertDialogAction
                          className="bg-rose-600 hover:bg-rose-700"
                          onClick={() => onRemoveRow(row.key)}
                          data-testid={`confirm-remove-judge-${idx}`}
                        >
                          Rimuovi
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                ) : (
                  // Empty row -> X removes the empty row directly (no confirm needed)
                  judgeRows.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="text-slate-400 hover:bg-slate-100 hover:text-slate-700 flex-shrink-0"
                      onClick={() => onRemoveRow(row.key)}
                      data-testid={`remove-empty-row-${idx}`}
                      aria-label="Rimuovi riga"
                    >
                      <X className="h-5 w-5" />
                    </Button>
                  )
                )}
              </div>
            );
          })}

          <button
            type="button"
            onClick={onAddRow}
            className="inline-flex items-center gap-2 mt-3 px-4 py-2.5 rounded-lg border-2 border-dashed border-slate-300 text-slate-600 hover:border-blue-700 hover:text-blue-700 hover:bg-blue-50 transition font-medium text-sm"
            data-testid="add-judge-row"
          >
            <Plus className="h-4 w-4" /> Aggiungi giudice
          </button>
        </div>
      </section>

      {/* Step 2 - Athletes */}
      <section className="bg-white rounded-2xl ring-1 ring-slate-200 p-6 sm:p-8 mb-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="h-9 w-9 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center font-display font-bold">2</div>
          <div>
            <h2 className="font-display text-xl font-semibold text-slate-900">Atleti partecipanti</h2>
            <p className="text-sm text-slate-500">{isLatin ? "Numero, atleta, tipo, classe e fascia di età." : "Inserisci gli atleti che parteciperanno alla Dance Analysis."}</p>
          </div>
        </div>

        <form
          onSubmit={onAddAthlete}
          className={`grid ${isLatin ? "grid-cols-12 gap-3" : "grid-cols-12 gap-3"} items-end bg-slate-50 rounded-xl p-4 border border-slate-200`}
          data-testid="add-athlete-form"
        >
          <div className={isLatin ? "col-span-6 sm:col-span-2" : "col-span-12 sm:col-span-2"}>
            <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Numero</Label>
            <Input
              type="number"
              min={1}
              max={600}
              placeholder="1-600"
              value={athleteForm.number}
              onChange={(e) => setAthleteForm({ ...athleteForm, number: e.target.value })}
              className="mt-2 h-11 bg-white font-mono"
              required
              data-testid="athlete-number-input"
            />
          </div>
          <div className={isLatin ? "col-span-12 sm:col-span-4" : "col-span-12 sm:col-span-5"}>
            <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Nome e Cognome / Coppia</Label>
            <Input
              placeholder="Mario Rossi"
              value={athleteForm.name}
              onChange={(e) => setAthleteForm({ ...athleteForm, name: e.target.value })}
              className="mt-2 h-11 bg-white"
              required
              data-testid="athlete-name-input"
            />
          </div>
          <div className={isLatin ? "col-span-6 sm:col-span-2" : "col-span-12 sm:col-span-3"}>
            <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Tipo</Label>
            <RadioGroup
              value={athleteForm.type}
              onValueChange={(v) => setAthleteForm({ ...athleteForm, type: v })}
              className="mt-2 flex items-center gap-3 h-11"
            >
              {(isLatin ? ["Singolo", "Duo"] : ["Singolo", "Duo", "Gruppo"]).map((t) => (
                <label key={t} className="flex items-center gap-1.5 text-sm cursor-pointer" data-testid={`athlete-type-${t}`}>
                  <RadioGroupItem value={t} />
                  <span>{t}</span>
                </label>
              ))}
            </RadioGroup>
          </div>
          {isLatin && (
            <>
              <div className="col-span-6 sm:col-span-2">
                <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Classe</Label>
                <Select value={athleteForm.classe} onValueChange={(v) => setAthleteForm({ ...athleteForm, classe: v })}>
                  <SelectTrigger className="mt-2 h-11 bg-white" data-testid="athlete-classe-select"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    {LATIN_CLASSI.map((c) => <SelectItem key={c} value={c} data-testid={`classe-opt-${c}`}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-6 sm:col-span-2">
                <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Fascia età</Label>
                <Select value={athleteForm.fascia_eta} onValueChange={(v) => setAthleteForm({ ...athleteForm, fascia_eta: v })}>
                  <SelectTrigger className="mt-2 h-11 bg-white" data-testid="athlete-fascia-select"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    {LATIN_FASCE.map((f) => <SelectItem key={f} value={f} data-testid={`fascia-opt-${f.replace('/','-')}`}>{f}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
          <div className={isLatin ? "col-span-12 sm:col-span-12" : "col-span-12 sm:col-span-2"}>
            <Button
              type="submit"
              disabled={adding}
              className="w-full h-11 bg-blue-700 hover:bg-blue-800 text-white"
              data-testid="add-athlete-button"
            >
              {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Plus className="h-4 w-4 mr-2" /> Inserisci</>)}
            </Button>
          </div>
        </form>

        <div className="mt-6 rounded-xl ring-1 ring-slate-200 overflow-hidden">
          <div className="bg-slate-50 px-5 py-3 text-xs uppercase tracking-wider text-slate-500 font-semibold flex items-center justify-between">
            <span>Elenco atleti — ordine cronologico per numero di gara</span>
            <span className="inline-flex items-center gap-1.5"><Users className="h-3.5 w-3.5" />{sortedAthletes.length}</span>
          </div>
          {sortedAthletes.length === 0 ? (
            <div className="p-6 text-sm text-slate-500 text-center">Nessun atleta inserito.</div>
          ) : (
            <table className="w-full text-left">
              <thead className="bg-white text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
                <tr>
                  <th className="px-5 py-3 font-semibold w-24">Numero</th>
                  <th className="px-5 py-3 font-semibold">Nome / Coppia</th>
                  <th className="px-5 py-3 font-semibold w-28">Tipo</th>
                  {isLatin && <th className="px-5 py-3 font-semibold w-24">Classe</th>}
                  {isLatin && <th className="px-5 py-3 font-semibold w-28">Fascia età</th>}
                  <th className="w-px"></th>
                </tr>
              </thead>
              <tbody>
                {sortedAthletes.map((a) => (
                  <tr key={a.id} className="border-t border-slate-100" data-testid={`athlete-row-${a.id}`}>
                    <td className="px-5 py-3 font-mono font-bold text-blue-700">{a.number}</td>
                    <td className="px-5 py-3 font-medium text-slate-900">{a.name}</td>
                    <td className="px-5 py-3">
                      <Badge variant="outline" className="border-slate-300 text-slate-700">{a.type}</Badge>
                    </td>
                    {isLatin && <td className="px-5 py-3 text-sm text-slate-700 font-semibold">{a.classe || "—"}</td>}
                    {isLatin && <td className="px-5 py-3 text-sm text-slate-700 font-semibold">{a.fascia_eta || "—"}</td>}
                    <td className="px-5 py-3 text-right">
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                            data-testid={`delete-athlete-${a.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Eliminare {a.name}?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Numero di gara {a.number} ({a.type}).
                              {isSent && " La modifica sarà visibile ai giudici subito."}
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Annulla</AlertDialogCancel>
                            <AlertDialogAction
                              className="bg-rose-600 hover:bg-rose-700"
                              onClick={() => onDeleteAthlete(a.id)}
                              data-testid={`confirm-delete-athlete-${a.id}`}
                            >
                              Elimina
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {/* Step 2.5 - Latin Style: dances + batches */}
      {isLatin && (
        <>
          <section className="bg-white rounded-2xl ring-1 ring-slate-200 p-6 sm:p-8 mb-6" data-testid="latin-dances-section">
            <div className="flex items-center gap-3 mb-5">
              <div className="h-9 w-9 rounded-lg bg-fuchsia-50 text-fuchsia-700 flex items-center justify-center">
                <Music className="h-5 w-5" />
              </div>
              <div>
                <h2 className="font-display text-xl font-semibold text-slate-900">Balli della competizione</h2>
                <p className="text-sm text-slate-500">Seleziona uno o più balli che comporranno la Dance Analysis Latin Style.</p>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
              {LATIN_DANCE_OPTIONS.map((d) => {
                const checked = compDances.includes(d.code);
                return (
                  <button
                    type="button"
                    key={d.code}
                    disabled={isSent || savingLatinConfig}
                    onClick={() => toggleDance(d.code)}
                    className={`flex items-center gap-2.5 rounded-xl border-2 px-3 py-2.5 text-left transition ${checked ? "border-fuchsia-700 bg-fuchsia-50" : "border-slate-200 hover:border-slate-300"} ${isSent ? "opacity-60 cursor-not-allowed" : ""}`}
                    data-testid={`dance-toggle-${d.code}`}
                  >
                    <Checkbox checked={checked} className="pointer-events-none" />
                    <span className="font-display font-semibold text-slate-900">{d.label}</span>
                  </button>
                );
              })}
            </div>
            {compDances.length === 0 && (
              <p className="text-xs text-amber-700 mt-3 flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5" /> Seleziona almeno un ballo per poter inviare la competizione.</p>
            )}
          </section>

          <section className="bg-white rounded-2xl ring-1 ring-slate-200 p-6 sm:p-8 mb-6" data-testid="latin-batches-section">
            <div className="flex items-center gap-3 mb-5 flex-wrap">
              <div className="h-9 w-9 rounded-lg bg-indigo-50 text-indigo-700 flex items-center justify-center">
                <Layers className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="font-display text-xl font-semibold text-slate-900">Batterie</h2>
                <p className="text-sm text-slate-500">Suddividi gli atleti in batterie. Puoi rigenerare e spostare manualmente.</p>
              </div>
              <Button
                onClick={onDownloadBatchesPdf}
                disabled={downloadingBatchesPdf || sortedAthletes.length === 0}
                variant="outline"
                className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                data-testid="download-batches-pdf-latin"
              >
                {downloadingBatchesPdf ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}
                Scarica elenco PDF
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
              <div>
                <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Suddivisione</Label>
                <RadioGroup
                  value={comp.batches_mode || "single"}
                  onValueChange={(v) => updateBatchesConfig({ batches_mode: v })}
                  className="mt-2 flex items-center gap-3"
                >
                  <label className="flex items-center gap-1.5 text-sm cursor-pointer" data-testid="batches-mode-single">
                    <RadioGroupItem value="single" disabled={isSent} /> <span>Batteria unica</span>
                  </label>
                  <label className="flex items-center gap-1.5 text-sm cursor-pointer" data-testid="batches-mode-split">
                    <RadioGroupItem value="split" disabled={isSent} /> <span>Suddividi in N batterie</span>
                  </label>
                </RadioGroup>
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Numero batterie (1-30)</Label>
                <Input
                  type="number"
                  min={1}
                  max={30}
                  disabled={isSent || (comp.batches_mode || "single") !== "split"}
                  value={comp.batches_count || 1}
                  onChange={(e) => {
                    const n = Math.max(1, Math.min(30, parseInt(e.target.value || "1", 10) || 1));
                    updateBatchesConfig({ batches_count: n });
                  }}
                  className="mt-2 h-11"
                  data-testid="batches-count-input"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Metodo di suddivisione</Label>
                <Select
                  value={comp.batches_split_by || "random"}
                  onValueChange={(v) => updateBatchesConfig({ batches_split_by: v })}
                  disabled={isSent || (comp.batches_mode || "single") !== "split"}
                >
                  <SelectTrigger className="mt-2 h-11" data-testid="batches-split-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SPLIT_MODES.map((m) => <SelectItem key={m.v} value={m.v} data-testid={`split-opt-${m.v}`}>{m.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex flex-wrap gap-3 items-center mb-4">
              <Button
                onClick={onGenerateBatches}
                disabled={generatingBatches || isSent || sortedAthletes.length === 0}
                className="bg-indigo-700 hover:bg-indigo-800 text-white"
                data-testid="generate-batches-button"
              >
                {generatingBatches ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                Genera batterie
              </Button>
              <span className="text-xs text-slate-500">
                {compBatches.length > 0 ? `${compBatches.length} batter${compBatches.length === 1 ? "ia" : "ie"} attive` : "Nessuna batteria generata"}
              </span>
            </div>

            {compBatches.length > 0 && (() => {
              const assignedIds = new Set(compBatches.flatMap((b) => b.athlete_ids || []));
              const unassigned = sortedAthletes.filter((a) => !assignedIds.has(a.id));
              if (unassigned.length === 0) return null;
              return (
                <div className="rounded-xl ring-1 ring-amber-300 bg-amber-50 p-4 mb-4" data-testid="unassigned-athletes-block">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="h-4 w-4 text-amber-700" />
                    <h4 className="font-display font-semibold text-amber-900 text-sm uppercase tracking-wider">
                      Atleti non assegnati a nessuna batteria ({unassigned.length})
                    </h4>
                  </div>
                  {isSent && (
                    <p className="text-xs text-amber-800 mb-3 italic" data-testid="unassigned-sent-notice">
                      La competizione è già stata inviata: assegnando un atleta a una batteria, l'aggiornamento sarà visibile ai giudici al loro prossimo refresh.
                    </p>
                  )}
                  <ul className="space-y-2">
                    {unassigned.map((a) => (
                      <li key={a.id} className="flex items-center gap-3 bg-white rounded-lg ring-1 ring-amber-200 px-3 py-2" data-testid={`unassigned-${a.id}`}>
                        <span className="font-mono font-bold text-blue-700 text-sm w-10">{a.number}</span>
                        <span className="text-sm font-medium text-slate-900 flex-1 truncate">{a.name}</span>
                        {a.classe && <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{a.classe} · {a.fascia_eta}</span>}
                        <Select onValueChange={(v) => onMoveAthlete(a.id, v)}>
                          <SelectTrigger className="h-9 w-44 text-xs bg-white" data-testid={`assign-athlete-${a.id}`}>
                            <SelectValue placeholder="Assegna a batteria…" />
                          </SelectTrigger>
                          <SelectContent>
                            {compBatches.map((bb) => (
                              <SelectItem key={bb.id} value={bb.id}>→ {bb.name}</SelectItem>
                            ))}
                            <SelectItem value="new" data-testid={`assign-new-batch-${a.id}`}>+ Nuova batteria</SelectItem>
                          </SelectContent>
                        </Select>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })()}

            {compBatches.length > 0 && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {compBatches.map((b) => {
                  const atletiBatch = (b.athlete_ids || [])
                    .map((aid) => sortedAthletes.find((a) => a.id === aid))
                    .filter(Boolean);
                  return (
                    <div key={b.id} className="rounded-xl ring-1 ring-slate-200 overflow-hidden bg-slate-50" data-testid={`batch-${b.id}`}>
                      <div className="bg-indigo-50 border-b border-indigo-100 px-4 py-3 flex items-center justify-between">
                        <h4 className="font-display font-semibold text-indigo-900">{b.name}</h4>
                        <Badge className="bg-indigo-600 hover:bg-indigo-600 text-white">{atletiBatch.length}</Badge>
                      </div>
                      {atletiBatch.length === 0 ? (
                        <div className="p-4 text-center text-xs text-slate-500 italic">Vuota</div>
                      ) : (
                        <ul className="divide-y divide-slate-200 max-h-72 overflow-y-auto">
                          {atletiBatch.map((a) => (
                            <li key={a.id} className="flex items-center gap-2 px-3 py-2 bg-white" data-testid={`batch-athlete-${a.id}`}>
                              <span className="font-mono font-bold text-blue-700 text-sm w-10">{a.number}</span>
                              <span className="text-sm font-medium text-slate-900 flex-1 truncate">{a.name}</span>
                              {a.classe && <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{a.classe}·{a.fascia_eta}</span>}
                              {compBatches.length > 1 && (
                                <Select onValueChange={(v) => onMoveAthlete(a.id, v)}>
                                  <SelectTrigger className="h-8 w-28 text-xs" data-testid={`move-athlete-${a.id}`}><SelectValue placeholder="Sposta…" /></SelectTrigger>
                                  <SelectContent>
                                    {compBatches.filter((bb) => bb.id !== b.id).map((bb) => (
                                      <SelectItem key={bb.id} value={bb.id}>→ {bb.name}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </>
      )}

      {!isLatin && sortedAthletes.length > 0 && (
        <section className="bg-white rounded-2xl ring-1 ring-slate-200 p-5 mb-6 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-slate-600 flex items-center gap-2">
            <FileText className="h-4 w-4 text-indigo-600" /> Scarica l'elenco atleti in PDF per il presentatore.
          </p>
          <Button
            onClick={onDownloadBatchesPdf}
            disabled={downloadingBatchesPdf}
            variant="outline"
            className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
            data-testid="download-batches-pdf-single"
          >
            {downloadingBatchesPdf ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}
            Scarica elenco PDF
          </Button>
        </section>
      )}

      {/* Step 3 - Send (only if not yet sent) */}
      {!isSent && (
        <section className="bg-gradient-to-br from-blue-700 to-indigo-700 text-white rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="font-display text-xl font-semibold">Pronto per inviare?</h2>
            <p className="text-blue-100 text-sm mt-1">
              Una volta inviata, la competizione comparirà ai giudici designati nella loro card Dance Analysis.
            </p>
          </div>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                size="lg"
                disabled={sending || !comp.judges?.length || !comp.athletes?.length}
                className="bg-white text-blue-700 hover:bg-blue-50 font-semibold h-12 px-6"
                data-testid="send-competition-button"
              >
                <Send className="h-4 w-4 mr-2" />
                INVIA COMPETIZIONE
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Confermi l'invio della competizione?</AlertDialogTitle>
                <AlertDialogDescription>
                  Tutti i giudici designati la vedranno nella loro area Dance Analysis. Potrai comunque modificare giudici e atleti in seguito: gli aggiornamenti saranno propagati ai destinatari.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="cancel-send">Annulla</AlertDialogCancel>
                <AlertDialogAction
                  onClick={onSend}
                  className="bg-blue-700 hover:bg-blue-800"
                  data-testid="confirm-send-competition"
                >
                  OK, invia
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </section>
      )}

      {/* Step 3b - Voting summary (only after send) */}
      {isSent && (
        <section className="bg-white rounded-2xl ring-1 ring-slate-200 p-6 sm:p-8" data-testid="voting-summary-section">
          <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center">
                <BarChart3 className="h-5 w-5" />
              </div>
              <div>
                <h2 className="font-display text-xl font-semibold text-slate-900">Riepilogo voti</h2>
                <p className="text-sm text-slate-500">
                  {summary
                    ? `${summary.finalized_count} di ${summary.judges_count} giudic${summary.judges_count === 1 ? "e" : "i"} ha${summary.finalized_count === 1 ? "" : "nno"} concluso la votazione.`
                    : "Voti ricevuti dai giudici per ogni atleta."}
                </p>
              </div>
            </div>
            <Button
              onClick={handleDownloadAll}
              disabled={downloadingZip || !summary?.athletes?.length}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="download-all-zip"
            >
              {downloadingZip ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              Genera Tutti gli Attestati
            </Button>
            <Button
              variant="outline"
              onClick={loadSummary}
              disabled={summaryLoading}
              data-testid="refresh-summary"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${summaryLoading ? "animate-spin" : ""}`} />
              Aggiorna
            </Button>
          </div>

          {!summary ? (
            <div className="p-8 text-center text-slate-500 text-sm">Caricamento riepilogo…</div>
          ) : summary.athletes.length === 0 ? (
            <div className="p-8 text-center text-slate-500 text-sm">Nessun atleta da visualizzare.</div>
          ) : (
            <div className="space-y-5">
              {summary.athletes.map((a) => {
                if (isLatin) {
                  const dances = summary.dances || [];
                  return (
                    <div key={a.id} className="rounded-xl ring-1 ring-slate-200 overflow-hidden" data-testid={`summary-athlete-${a.id}`}>
                      <div className="bg-slate-50 px-5 py-3 flex flex-wrap items-center gap-x-5 gap-y-1 border-b border-slate-200">
                        <span className="font-mono font-bold text-blue-700 text-lg">{a.number}</span>
                        <span className="font-display font-semibold text-slate-900 flex-1 min-w-0">{a.name}</span>
                        <Badge variant="outline" className="border-slate-300 text-slate-700">{a.type}</Badge>
                        {a.classe && <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100">{a.classe} · {a.fascia_eta}</Badge>}
                        {a.grand_total != null && (
                          <span className="font-display font-bold tabular-nums text-white bg-blue-700 px-3 py-1 rounded-md" data-testid={`grand-total-${a.id}`}>TOT {a.grand_total}</span>
                        )}
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-left min-w-[640px]">
                          <thead className="bg-white text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
                            <tr>
                              <th className="px-5 py-3 font-semibold">Ballo</th>
                              <th className="px-5 py-3 font-semibold w-28 text-center">Media Tec.</th>
                              <th className="px-5 py-3 font-semibold w-28 text-center">Media Cor.</th>
                              <th className="px-5 py-3 font-semibold w-28 text-center">Media Mus.</th>
                              <th className="px-5 py-3 font-semibold w-28 text-center">Totale</th>
                            </tr>
                          </thead>
                          <tbody>
                            {dances.map((d) => {
                              const av = a.dance_averages?.[d] || {};
                              const danceLabel = (summary.dance_labels && summary.dance_labels[d]) || d;
                              const fmt = (v) => v == null ? <span className="text-slate-300">—</span>
                                : Number.isInteger(v) ? v : v.toFixed(2).replace(/\.?0+$/, "");
                              return (
                                <tr key={d} className="border-t border-slate-100">
                                  <td className="px-5 py-3 font-semibold text-fuchsia-700">{danceLabel}</td>
                                  <td className="px-5 py-3 text-center font-display font-bold tabular-nums text-emerald-800">{fmt(av.tecnica)}</td>
                                  <td className="px-5 py-3 text-center font-display font-bold tabular-nums text-emerald-800">{fmt(av.coreografia)}</td>
                                  <td className="px-5 py-3 text-center font-display font-bold tabular-nums text-emerald-800">{fmt(av.musicalita)}</td>
                                  <td className="px-5 py-3 text-center">
                                    {av.totale != null ? (
                                      <span className="font-display font-bold tabular-nums text-blue-700 bg-blue-50 px-3 py-1 rounded-md">{fmt(av.totale)}</span>
                                    ) : <span className="text-slate-300">—</span>}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                }
                // SINGOLO (existing)
                return (
                <div key={a.id} className="rounded-xl ring-1 ring-slate-200 overflow-hidden" data-testid={`summary-athlete-${a.id}`}>
                  <div className="bg-slate-50 px-5 py-3 flex flex-wrap items-center gap-x-5 gap-y-1 border-b border-slate-200">
                    <span className="font-mono font-bold text-blue-700 text-lg">{a.number}</span>
                    <span className="font-display font-semibold text-slate-900 flex-1 min-w-0">{a.name}</span>
                    <Badge variant="outline" className="border-slate-300 text-slate-700">{a.type}</Badge>
                    <Button
                      size="sm"
                      onClick={() => handleDownloadAthlete(a.id, a.name)}
                      disabled={downloadingId === a.id}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid={`download-cert-${a.id}`}
                    >
                      {downloadingId === a.id ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileBadge className="h-4 w-4 mr-1.5" />}
                      Attestato
                    </Button>
                  </div>
                  <table className="w-full text-left">
                    <thead className="bg-white text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
                      <tr>
                        <th className="px-5 py-3 font-semibold">Giudice</th>
                        <th className="px-5 py-3 font-semibold w-28 text-center">Tecnica</th>
                        <th className="px-5 py-3 font-semibold w-28 text-center">Coreografia</th>
                        <th className="px-5 py-3 font-semibold w-28 text-center">Musicalità</th>
                        <th className="px-5 py-3 font-semibold w-28 text-center">Totale</th>
                      </tr>
                    </thead>
                    <tbody>
                      {a.judges.map((j) => (
                        <tr key={j.judge_id} className="border-t border-slate-100">
                          <td className="px-5 py-3">
                            <div className="font-medium text-slate-900 inline-flex items-center gap-2">
                              {j.judge_name}
                              {j.finalized ? (
                                <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 gap-1 text-xs">
                                  <CheckCircle2 className="h-3 w-3" /> Concluso
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-50 gap-1 text-xs">
                                  <Clock className="h-3 w-3" /> In attesa
                                </Badge>
                              )}
                            </div>
                          </td>
                          {["tecnica", "coreografia", "musicalita"].map((k) => (
                            <td key={k} className="px-5 py-3 text-center">
                              {j.scores[k] != null ? (
                                <span className="font-display font-bold text-lg tabular-nums text-slate-900">{j.scores[k]}</span>
                              ) : (
                                <span className="text-slate-300">—</span>
                              )}
                            </td>
                          ))}
                          <td className="px-5 py-3 text-center">
                            {j.total != null ? (
                              <span className="font-display font-bold text-lg tabular-nums text-blue-700 bg-blue-50 px-3 py-1 rounded-md">
                                {j.total}
                              </span>
                            ) : (
                              <span className="text-slate-300">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-emerald-50 border-t-2 border-emerald-200">
                        <td className="px-5 py-3 font-display font-bold text-emerald-900 text-sm uppercase tracking-wider">
                          Media
                        </td>
                        {["tecnica", "coreografia", "musicalita"].map((k) => (
                          <td key={k} className="px-5 py-3 text-center">
                            {a.averages?.[k] != null ? (
                              <span className="font-display font-bold text-base tabular-nums text-emerald-800">
                                {Number.isInteger(a.averages[k]) ? a.averages[k] : a.averages[k].toFixed(2).replace(/\.?0+$/, "")}
                              </span>
                            ) : (
                              <span className="text-slate-300">—</span>
                            )}
                          </td>
                        ))}
                        <td className="px-5 py-3"></td>
                      </tr>
                      <tr className="bg-blue-50 border-t-2 border-blue-200">
                        <td className="px-5 py-3 font-display font-bold text-blue-900 text-sm uppercase tracking-wider" colSpan={4}>
                          Totale Generale
                        </td>
                        <td className="px-5 py-3 text-center">
                          {a.grand_total != null ? (
                            <span
                              className="font-display font-bold text-xl tabular-nums text-white bg-blue-700 px-4 py-1.5 rounded-md inline-block min-w-[3rem]"
                              data-testid={`grand-total-${a.id}`}
                            >
                              {a.grand_total}
                            </span>
                          ) : (
                            <span className="text-slate-400 italic text-sm">In attesa</span>
                          )}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                );
              })}
            </div>
          )}
        </section>
      )}
    </Layout>
  );
}
